import { EditOutlined } from '@ant-design/icons';
import { Box, Button, Dialog, Stack } from '@mui/material';
import CityDto from 'common/dto-types/city-dto';
import IconButton from 'components/@extended/IconButton';
import MainCard from 'components/MainCard';
import ScrollX from 'components/ScrollX';
import BaseTable from 'components/base-table';
import { BaseTableFetchFuncProps } from 'components/base-table/dto';
import { useMemo, useState } from 'react';
import { Row } from 'react-table';
import { mapFiltersToSpecification, mapSearchParamToSpecification } from 'utils/filtering/map-filter-to-specification';
import { PopupTransition } from '../../components/@extended/Transitions';
import { dispatch, useSelector } from '../../store';
import {
    closeForm,
    openCreateForm,
    setCity,
    setCityColumnsPersistence,
    setCityListPersistence,
    setCityListTournamentFilter,
} from '../../store/reducers/city';
import { fetchCityList } from './api';
import CityDeleteButton from './city-delete-button';
import CityTableColumnConfig, { SearchableCityTableColumnConfig } from './city-column-config';
import CityForm from './city-form';
import AsyncSelectSearchField from 'components/async-select-search-field';
import FDCPServices from 'fdcp/fdcp-services';
import TournamentDto, {
    mapFdcpSeasonToTournamentDto,
    sortTournamentsByStartDateDesc,
} from 'common/dto-types/tournament-dto';

type HostCityRow = CityDto & { tournamentNames: string };

const searchTournamentOptions = (inputValue: string): Promise<TournamentDto[]> =>
    FDCPServices.searchSeasonByName({ searchString: inputValue }).then((seasons) =>
        seasons.map(mapFdcpSeasonToTournamentDto),
    );

const CityListPage = () => {
    const [cityList, setCityList] = useState<Array<HostCityRow>>([]);
    const [pageCount, setPageCount] = useState(0);
    const savedFilters = useSelector((state) => state.city.filters);
    const savedSorting = useSelector((state) => state.city.sorting);
    const savedPagination = useSelector((state) => state.city.pagination);
    const defaultVisibleColumnConfig = useSelector((state) => state.city.visibleColumns);
    const tournamentFilter = useSelector((state) => state.city.tournamentFilter);

    const isFormOpen = useSelector((state) => state.city.isFormOpen);

    const fetchData: BaseTableFetchFuncProps = async ({
        pageSize,
        pageIndex,
        filters,
        searchTerm,
        sortBy,
        sortingType,
    }) => {
        const fetchFilters = tournamentFilter
            ? [...filters, { id: 'Tournament', value: tournamentFilter.fdcpId }]
            : filters;

        const data = await fetchCityList({
            pageIndex,
            pageSize,
            sortBy,
            sortingType,
            andSpecifications: mapFiltersToSpecification(fetchFilters),
            orSpecifications:
                searchTerm && searchTerm.length > 0
                    ? mapSearchParamToSpecification(searchTerm, SearchableCityTableColumnConfig)
                    : undefined,
        });

        if (data) {
            const newPageCount = Math.ceil(data.total / pageSize);
            setCityList(
                data.elements.map((city) => ({
                    ...city,
                    tournamentNames: sortTournamentsByStartDateDesc(city.tournaments)
                        .map((tournament) => tournament.name)
                        .join('\n'),
                })),
            );
            setPageCount(newPageCount);

            dispatch(
                setCityListPersistence({
                    filters: filters,
                    sorting: { searchTerm, sortBy, sortingType },
                    pagination: { pageSize, pageIndex },
                }),
            );

            if (data.total > 0 && data.elements.length === 0) {
                await fetchData({
                    pageSize: pageSize,
                    pageIndex: newPageCount - 1,
                    sortingType: sortingType,
                    sortBy: sortBy,
                    filters: filters,
                    searchTerm: searchTerm,
                });
            }
        }
    };

    const handleColumnVisibilityChange = async (newVisibleColumns: string[]) => {
        dispatch(setCityColumnsPersistence(newVisibleColumns));
    };

    const refreshCityList = async () => {
        await fetchData({
            pageSize: savedPagination.pageSize,
            pageIndex: savedPagination.pageIndex,
            filters: savedFilters,
            searchTerm: savedSorting.searchTerm,
            sortBy: savedSorting.sortBy,
            sortingType: savedSorting.sortingType,
        });
        dispatch(closeForm());
    };

    const defaultColumnConfig = useMemo(
        () => [
            ...CityTableColumnConfig,
            {
                Header: 'Actions',
                Cell: (row: { row: Row }) => {
                    const city = row.row.original as HostCityRow;
                    return (
                        <Stack direction="row">
                            <IconButton
                                shape="rounded"
                                onClick={() => {
                                    dispatch(setCity(city));
                                    dispatch(openCreateForm());
                                }}
                            >
                                <EditOutlined />
                            </IconButton>
                            <CityDeleteButton city={city} refreshCallback={refreshCityList} />
                        </Stack>
                    );
                },
            },
        ],
        [],
    );

    return (
        <MainCard content={false}>
            <Stack direction="column">
                <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Button variant="contained" color="primary" onClick={() => dispatch(openCreateForm())}>
                        Add City
                    </Button>
                    <Box sx={{ minWidth: 400, maxWidth: 500 }}>
                        <AsyncSelectSearchField
                            fetchData={searchTournamentOptions}
                            getOptionLabel={(option) => `${option.name} - [${option.fdcpId}]`}
                            isOptionEqualToValue={(option, selectedValue) => option.fdcpId === selectedValue.fdcpId}
                            value={tournamentFilter}
                            setValue={(tournament) => dispatch(setCityListTournamentFilter(tournament))}
                        />
                    </Box>
                </Box>
                <ScrollX>
                    <BaseTable
                        data={cityList}
                        fetchData={fetchData}
                        pageCount={pageCount}
                        defaultFilters={savedFilters ?? []}
                        defaultPageSize={savedPagination.pageSize}
                        defaultPageIndex={savedPagination.pageIndex}
                        defaultSortingType={savedSorting.sortingType}
                        defaultSearchTerm={savedSorting.searchTerm}
                        defaultColumnIdToSortBy={savedSorting.sortBy}
                        defaultColumnConfig={defaultColumnConfig}
                        defaultVisibleColumnConfig={defaultVisibleColumnConfig}
                        key={tournamentFilter?.fdcpId ?? 0}
                        onColumnsVisibilityChange={handleColumnVisibilityChange}
                    />
                </ScrollX>
            </Stack>
            {isFormOpen && (
                <Dialog
                    maxWidth="sm"
                    TransitionComponent={PopupTransition}
                    fullWidth
                    open={isFormOpen}
                    sx={{ '& .MuiDialog-paper': { p: 0 } }}
                >
                    <CityForm refreshCallback={() => refreshCityList()} />
                </Dialog>
            )}
        </MainCard>
    );
};

export default CityListPage;
